<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <h3>📋 Détails du Prospect</h3>
          <p class="prospect-id">ID: {{ prospect?._id || 'N/A' }}</p>
        </div>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <form @submit.prevent="saveChanges">
          <!-- Informations Client -->
          <div class="section" :class="{ 'section-collapsed': !expandedSections.client }">
            <div class="section-header" @click="toggleSection('client')">
              <div class="section-icon">
                <i class="fas fa-user"></i>
              </div>
              <h4>👤 Informations Client</h4>
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </div>
            <div class="section-content" v-show="expandedSections.client">
              <div class="form-row">
                <div class="form-group full-width">
                  <label>Nom Complet *</label>
                  <input 
                    v-model="editableProspect.client.nomComplet"
                    type="text"
                    class="form-control"
                    :readonly="!canEdit"
                    :class="{ 'readonly': !canEdit }"
                    placeholder="Nom et prénom du client"
                  >
                </div>
              </div>
              
              <div class="form-row">
                <div class="form-group">
                  <label>Téléphone *</label>
                  <input 
                    v-model="editableProspect.client.telephone"
                    type="tel"
                    class="form-control"
                    :readonly="!canEdit"
                    :class="{ 'readonly': !canEdit }"
                  >
                </div>
                <div class="form-group">
                  <label>Email</label>
                  <input 
                    v-model="editableProspect.client.email"
                    type="email"
                    class="form-control"
                    :readonly="!canEdit"
                    :class="{ 'readonly': !canEdit }"
                  >
                </div>
              </div>

              <div class="form-group">
                <label>Adresse</label>
                <input 
                  v-model="editableProspect.client.adresse.rue"
                  type="text"
                  placeholder="Rue"
                  class="form-control"
                  :readonly="!canEdit"
                  :class="{ 'readonly': !canEdit }"
                >
                <div class="form-row" style="margin-top: 0.5rem;">
                  <input 
                    v-model="editableProspect.client.adresse.codePostal"
                    type="text"
                    placeholder="Code postal"
                    class="form-control"
                    style="flex: 1;"
                    :readonly="!canEdit"
                    :class="{ 'readonly': !canEdit }"
                  >
                  <div class="gouvernorat-select-wrapper" style="flex: 2;" :class="{ 'has-selection': editableProspect.client.adresse.ville, 'disabled': !canEdit }">
                    <div class="gouvernorat-select-icon">
                      <i class="fas fa-map-marker-alt"></i>
                    </div>
                    <select 
                      v-model="editableProspect.client.adresse.ville"
                      class="form-control gouvernorat-select"
                      :disabled="!canEdit"
                    >
                      <option value="">📍 Sélectionner un ville</option>
                      <optgroup label="🗺️ Nord-Est">
                        <option value="Tunis">Tunis</option>
                        <option value="Ariana">Ariana</option>
                        <option value="Ben Arous">Ben Arous</option>
                        <option value="Manouba">Manouba</option>
                        <option value="Zaghouan">Zaghouan</option>
                        <option value="Bizerte">Bizerte</option>
                      </optgroup>
                      <optgroup label="🗺️ Nord-Ouest">
                        <option value="Jendouba">Jendouba</option>
                        <option value="Béja">Béja</option>
                        <option value="Le Kef">Le Kef</option>
                        <option value="Siliana">Siliana</option>
                      </optgroup>
                      <optgroup label="🗺️ Centre-Est">
                        <option value="Nabeul">Nabeul</option>
                        <option value="Sousse">Sousse</option>
                        <option value="Monastir">Monastir</option>
                        <option value="Mahdia">Mahdia</option>
                      </optgroup>
                      <optgroup label="🗺️ Sud">
                        <option value="Sfax">Sfax</option>
                        <option value="Gabès">Gabès</option>
                        <option value="Médenine">Médenine</option>
                        <option value="Tataouine">Tataouine</option>
                        <option value="Kairouan">Kairouan</option>
                        <option value="Kasserine">Kasserine</option>
                        <option value="Sidi Bouzid">Sidi Bouzid</option>
                        <option value="Gafsa">Gafsa</option>
                        <option value="Tozeur">Tozeur</option>
                        <option value="Kébili">Kébili</option>
                      </optgroup>
                    </select>
                    <div class="gouvernorat-select-arrow">
                      <i class="fas fa-chevron-down"></i>
                    </div>
                    <div v-if="editableProspect.client.adresse.ville && canEdit" class="gouvernorat-selection-indicator">
                      <i class="fas fa-check-circle"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Détails Intervention -->
          <div class="section" :class="{ 'section-collapsed': !expandedSections.intervention }">
            <div class="section-header" @click="toggleSection('intervention')">
              <div class="section-icon">
                <i class="fas fa-tools"></i>
              </div>
              <h4>🔧 Détails de l'Operation</h4>
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </div>
            <div class="section-content" v-show="expandedSections.intervention">
              <!-- Date RDV - Mode Lecture -->
              <div class="form-row" v-if="!canEdit && getDateRdvInfo()">
                <div class="form-group full-width">
                  <label>📅 Date RDV</label>
                  <div class="date-rdv-display">
                    <div class="date-rdv-main">
                      <i class="fas fa-calendar-check"></i>
                      <span class="date-value">{{ formatDateRdvDisplay(getDateRdvInfo()) }}</span>
                    </div>
                    <div class="date-rdv-time" v-if="getDateRdvInfo().heureDebut">
                      <i class="fas fa-clock"></i>
                      <span>{{ getDateRdvInfo().heureDebut }} - {{ getDateRdvInfo().heureFin || '?' }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="form-row" v-else-if="!canEdit">
                <div class="form-group full-width">
                  <label>📅 Date RDV</label>
                  <div class="no-date-rdv">
                    <i class="fas fa-calendar-times"></i>
                    <span>Non planifié</span>
                  </div>
                </div>
              </div>
              
              <!-- Date RDV - Mode Édition -->
              <div class="form-row" v-if="canEdit">
                <div class="form-group full-width">
                  <label>📅 Date RDV</label>
                  <div class="date-rdv-edit-form">
                    <div class="date-rdv-edit-row">
                      <div class="date-edit-group">
                        <label><i class="fas fa-calendar-day"></i> Date</label>
                        <input 
                          v-model="editableDateRdv.date" 
                          type="date" 
                          class="form-control date-input"
                        />
                      </div>
                      <div class="date-edit-group">
                        <label><i class="fas fa-clock"></i> Heure début</label>
                        <input 
                          v-model="editableDateRdv.heureDebut" 
                          type="time" 
                          class="form-control time-input"
                        />
                      </div>
                      <div class="date-edit-group">
                        <label><i class="fas fa-clock"></i> Heure fin</label>
                        <input 
                          v-model="editableDateRdv.heureFin" 
                          type="time" 
                          class="form-control time-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Type d'intervention</label>
                  <select 
                    v-model="editableProspect.typeIntervention" 
                    class="form-control"
                    :disabled="!canEdit"
                    :class="{ 'readonly': !canEdit }"
                  >
                    <option value="Installation">🔧 Installation</option>
                    <option value="Intervention">⚙️ Intervention</option>
                    <option value="Prospect">💼 Prospect</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Priorité</label>
                  <select 
                    v-model="editableProspect.priorite" 
                    class="form-control"
                    :disabled="!canEdit"
                    :class="{ 'readonly': !canEdit }"
                  >
                    <option value="Basse">🟢 Basse</option>
                    <option value="Normale">🟡 Normale</option>
                    <option value="Haute">🟠 Haute</option>
                    <option value="Urgente">🔴 Urgente</option>
                  </select>
                </div>
              </div>

              <!-- Debug: Afficher les données brutes (désactivé) -->
              <div v-if="false" style="background: #f0f0f0; padding: 10px; margin-bottom: 10px; font-size: 12px; border-radius: 8px;">
                <strong>🔍 Debug Info:</strong><br>
                <strong>appareils existe:</strong> {{ !!editableProspect.appareils }}<br>
                <strong>appareils.length:</strong> {{ editableProspect.appareils?.length || 0 }}<br>
                <strong>appareils data:</strong> {{ JSON.stringify(editableProspect.appareils) }}<br>
                <strong>typeAppareil:</strong> {{ editableProspect.typeAppareil }}<br>
                <strong>nombreAppareils:</strong> {{ editableProspect.nombreAppareils }}
              </div>

              <!-- Prix RDV -->
              <div class="form-row">
                <div class="form-group">
                  <label>💰 Prix RDV (DH)</label>
                  <input 
                    v-model.number="editableProspect.prix"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control"
                    :readonly="!canEdit"
                    :class="{ 'readonly': !canEdit }"
                    placeholder="Prix du rendez-vous"
                  >
                </div>
              </div>

              <!-- Liste des appareils demandés -->
              <div class="appareils-section">
                <div class="appareils-header">
                  <label class="appareils-label">
                    <i class="fas fa-list"></i>
                    Appareils demandés ({{ getTotalAppareils() }})
                  </label>
                  <button 
                    v-if="canEdit" 
                    @click="addNewAppareil" 
                    type="button"
                    class="btn-add-appareil"
                  >
                    <i class="fas fa-plus"></i>
                    Ajouter un appareil
                  </button>
                </div>
                <div v-if="editableProspect.appareils && editableProspect.appareils.length > 0" class="appareils-grid">
                  <div 
                    v-for="(appareil, index) in editableProspect.appareils" 
                    :key="index"
                    class="appareil-card"
                  >
                    <div class="appareil-icon-display">{{ getAppareilIcon(appareil.type) }}</div>
                    <div class="appareil-details" v-if="!canEdit">
                      <div class="appareil-type">{{ appareil.type }}</div>
                      <div class="appareil-qty">Quantité: <strong>{{ appareil.quantite }}</strong></div>
                    </div>
                    <div class="appareil-edit-form" v-else>
                      <select v-model="appareil.type" class="form-control appareil-type-select">
                        <option value="GPS">📡 GPS</option>
                        <option value="SIM">📱 SIM</option>
                        <option value="Cable">🔌 Cable</option>
                        <option value="Boitier">📦 Boitier</option>
                        <option value="Antenne">📶 Antenne</option>
                        <option value="Capteur">🔍 Capteur</option>
                        <option value="Autre">🔧 Autre</option>
                      </select>
                      <input 
                        v-model.number="appareil.quantite" 
                        type="number" 
                        min="1" 
                        class="form-control appareil-qty-input"
                        placeholder="Qté"
                      >
                      <button 
                        @click="removeAppareil(index)" 
                        type="button"
                        class="btn-remove-appareil"
                        title="Supprimer cet appareil"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="no-appareils">
                  <i class="fas fa-box-open"></i>
                  <p>Aucun appareil demandé</p>
                  <button v-if="canEdit" @click="addNewAppareil" type="button" class="btn-add-first-appareil">
                    <i class="fas fa-plus"></i>
                    Ajouter le premier appareil
                  </button>
                </div>
              </div>



              <div class="form-group">
                <label>Description</label>
                <textarea 
                  v-model="editableProspect.description"
                  rows="3"
                  class="form-control"
                  :readonly="!canEdit"
                  :class="{ 'readonly': !canEdit }"
                ></textarea>
              </div>

              <div class="form-group">
                <label>Commentaires</label>
                <textarea 
                  v-model="editableProspect.commentaires"
                  rows="2"
                  class="form-control"
                  :readonly="!canEdit"
                  :class="{ 'readonly': !canEdit }"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Statut et Affectation -->
          <div class="section" :class="{ 'section-collapsed': !expandedSections.statut }">
            <div class="section-header" @click="toggleSection('statut')">
              <div class="section-icon">
                <i class="fas fa-flag"></i>
              </div>
              <h4>📊 Statut et Affectation</h4>
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </div>
            <div class="section-content" v-show="expandedSections.statut">
              <div class="form-row">
                <div class="form-group">
                  <label>Statut</label>
                  <div class="status-display">
                    <span :class="getStatutBadgeClass(editableProspect?.statut)">
                      {{ getStatutIcon(editableProspect?.statut) }} {{ editableProspect?.statut }}
                    </span>
                  </div>
                </div>
                <div class="form-group">
                  <label>Technicien affecté</label>
                  <div v-if="!canEdit" class="technicien-display">
                    <i class="fas fa-user-cog"></i>
                    {{ getDisplayTechnicienName() }}
                  </div>
                  <div v-else class="technicien-select-wrapper">
                    <select 
                      v-model="editableProspect.technicienAffecte" 
                      class="form-control technicien-select"
                    >
                      <option :value="null">Aucun technicien</option>
                      <option 
                        v-for="tech in techniciens" 
                        :key="tech._id" 
                        :value="tech._id"
                      >
                        👨‍🔧 {{ tech.prenom }} {{ tech.nom }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Informations Système -->
          <div class="section" :class="{ 'section-collapsed': !expandedSections.systeme }">
            <div class="section-header" @click="toggleSection('systeme')">
              <div class="section-icon">
                <i class="fas fa-info-circle"></i>
              </div>
              <h4>ℹ️ Informations Système</h4>
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </div>
            <div class="section-content" v-show="expandedSections.systeme">
              <div class="form-row">
                <div class="form-group">
                  <label>Date de création</label>
                  <div class="info-display">
                    <i class="fas fa-calendar-plus"></i>
                    {{ formatDateTime(editableProspect?.dateCreation) }}
                  </div>
                </div>
                <div class="form-group" v-if="editableProspect?.dateModification">
                  <label>Dernière modification</label>
                  <div class="info-display">
                    <i class="fas fa-edit"></i>
                    {{ formatDateTime(editableProspect?.dateModification) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <div class="footer-left">
          <button @click="closeModal" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Fermer
          </button>
        </div>
        
        <div class="footer-right" v-if="canEdit">
          <button 
            @click="saveChanges"
            class="btn btn-success"
            :disabled="saving"
          >
            <i class="fas fa-save"></i>
            {{ saving ? 'Sauvegarde...' : 'Sauvegarder' }}
          </button>
        </div>
        
        <div class="footer-right" v-else>
          <div class="readonly-notice">
            <i class="fas fa-eye"></i>
            Mode lecture seule
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'

export default {
  name: 'ProspectDetailModal',
  props: {
    prospect: {
      type: Object,
      required: true
    },
    userRole: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'prospect-updated'],
  setup(props, { emit }) {
    const store = useStore()
    const toast = useToast()
    
    const saving = ref(false)
    
    // État des sections (toutes fermées par défaut)
    const expandedSections = ref({
      client: false,
      intervention: false,
      statut: false,
      creneaux: false,
      systeme: false
    })

    // Fonction pour basculer une section
    const toggleSection = (sectionName) => {
      expandedSections.value[sectionName] = !expandedSections.value[sectionName]
    }
    const editableProspect = ref({
      client: {
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        adresse: {
          rue: '',
          ville: '',
          codePostal: '',
          region: ''
        }
      },
      typeIntervention: '',
      priorite: 'Normale',
      typeAppareil: '',
      nombreAppareils: 1,
      appareils: [], // Liste des appareils (nouveau format)
      prix: 0, // Prix du RDV
      description: '',
      commentaires: '',
      statut: 'En attente',
      datesProposes: [],
      dateCreation: new Date(),
      dateModification: null,
      technicienAffecte: null
    })
    const techniciens = ref([])
    
    // Date RDV éditable
    const editableDateRdv = ref({
      date: '',
      heureDebut: '09:00',
      heureFin: '12:00'
    })

    // Permissions basées sur le rôle
    const canEdit = computed(() => {
      return props.userRole === 'Admin' || props.userRole === 'Responsable Planning'
    })

    // Fonction utilitaire pour convertir une date ISO en format YYYY-MM-DD local
    const formatDateForInput = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      // Utiliser les méthodes locales pour éviter le décalage de fuseau horaire
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    // Initialiser les données éditables
    const initializeData = () => {
      if (props.prospect) {
        // Copie profonde du prospect avec valeurs par défaut
        const prospectCopy = JSON.parse(JSON.stringify(props.prospect))
        
        // Convertir les dates des créneaux proposés au bon format pour les inputs
        // IMPORTANT: Utiliser dateSortie (date réelle du RDV) si disponible, sinon date
        const datesProposeesFormatees = (prospectCopy.datesProposes || []).map(creneau => {
          // La date réelle du RDV est dans dateSortie, pas dans date
          const dateRdv = creneau.dateSortie || creneau.date
          return {
            ...creneau,
            date: formatDateForInput(dateRdv),
            dateSortie: formatDateForInput(dateRdv) // Garder aussi dateSortie formaté
          }
        })
        
        // Extraire l'ID du technicien si c'est un objet populé
        let technicienId = prospectCopy.technicienAffecte
        if (technicienId && typeof technicienId === 'object') {
          technicienId = technicienId._id
        }
        
        // S'assurer que les objets nécessaires existent avec des valeurs par défaut
        editableProspect.value = {
          ...editableProspect.value,
          ...prospectCopy,
          client: {
            nom: '',
            prenom: '',
            telephone: '',
            email: '',
            adresse: {
              rue: '',
              ville: '',
              codePostal: '',
              region: ''
            },
            ...(prospectCopy.client || {})
          },
          datesProposes: datesProposeesFormatees,
          appareils: prospectCopy.appareils || [], // S'assurer que la liste des appareils est copiée
          technicienAffecte: technicienId // Utiliser l'ID extrait
        }

        // S'assurer que l'adresse existe
        if (!editableProspect.value.client.adresse) {
          editableProspect.value.client.adresse = {
            rue: '',
            ville: '',
            codePostal: '',
            region: ''
          }
        }

        // Debug: Afficher les appareils chargés
        console.log('Appareils chargés dans le modal:', editableProspect.value.appareils)
        
        // Initialiser la date RDV éditable
        initializeDateRdv()
      }
    }
    
    // Initialiser la date RDV éditable à partir des données existantes
    const initializeDateRdv = () => {
      const prospect = props.prospect
      if (!prospect) return
      
      // Priorité 1 : RDV existant
      if (prospect.rdvExistant) {
        editableDateRdv.value = {
          date: formatDateForInput(prospect.rdvExistant.dateRdv),
          heureDebut: prospect.rdvExistant.heureDebut || '09:00',
          heureFin: prospect.rdvExistant.heureFin || '12:00'
        }
        return
      }
      
      // Priorité 2 : Première date proposée
      if (prospect.datesProposes && prospect.datesProposes.length > 0) {
        const premiereDatePropose = prospect.datesProposes[0]
        editableDateRdv.value = {
          date: formatDateForInput(premiereDatePropose.dateSortie || premiereDatePropose.date),
          heureDebut: premiereDatePropose.heureDebut || '09:00',
          heureFin: premiereDatePropose.heureFin || '12:00'
        }
        return
      }
      
      // Pas de date existante - valeurs par défaut (demain)
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      editableDateRdv.value = {
        date: formatDateForInput(tomorrow),
        heureDebut: '09:00',
        heureFin: '12:00'
      }
    }

    // Charger la liste des techniciens
    const loadTechniciens = async () => {
      try {
        const token = localStorage.getItem('token')
        console.log('🔍 Chargement des techniciens...')
        
        const response = await fetch('/api/prospects/techniciens', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        console.log('🔍 Réponse techniciens:', response.status)
        
        if (response.ok) {
          const result = await response.json()
          console.log('🔍 Techniciens reçus:', result)
          techniciens.value = result.techniciens || []
          console.log('🔍 Techniciens chargés:', techniciens.value.length)
        } else {
          console.error('❌ Erreur API techniciens:', response.status)
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement des techniciens:', error)
      }
    }

    // Sauvegarder les modifications
    const saveChanges = async () => {
      if (!canEdit.value) return

      saving.value = true
      try {
        const token = localStorage.getItem('token')
        
        // Mettre à jour la première date proposée avec la date RDV éditée
        if (editableDateRdv.value.date) {
          // Convertir la date au format ISO pour le backend
          const dateRdvISO = new Date(editableDateRdv.value.date).toISOString()
          
          console.log('📅 Date RDV à sauvegarder:', {
            dateInput: editableDateRdv.value.date,
            dateISO: dateRdvISO,
            heureDebut: editableDateRdv.value.heureDebut,
            heureFin: editableDateRdv.value.heureFin,
            hasRdvExistant: !!props.prospect?.rdvExistant
          })
          
          // Si un RDV existant existe, mettre à jour le RDV via l'API
          if (props.prospect?.rdvExistant?._id) {
            console.log('📅 Mise à jour du RDV existant:', props.prospect.rdvExistant._id)
            try {
              const rdvResponse = await fetch(`/api/rdv/${props.prospect.rdvExistant._id}/modifier-date`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  dateRdv: dateRdvISO,
                  heureDebut: editableDateRdv.value.heureDebut,
                  heureFin: editableDateRdv.value.heureFin
                })
              })
              
              if (rdvResponse.ok) {
                console.log('✅ RDV mis à jour avec succès')
              } else {
                console.warn('⚠️ Erreur mise à jour RDV:', await rdvResponse.text())
              }
            } catch (rdvError) {
              console.error('❌ Erreur mise à jour RDV:', rdvError)
            }
          }
          
          // Toujours mettre à jour datesProposes aussi
          if (!editableProspect.value.datesProposes || editableProspect.value.datesProposes.length === 0) {
            // Créer une nouvelle date proposée si aucune n'existe
            editableProspect.value.datesProposes = [{
              date: dateRdvISO,
              dateSortie: dateRdvISO,
              heureDebut: editableDateRdv.value.heureDebut,
              heureFin: editableDateRdv.value.heureFin,
              disponible: true
            }]
          } else {
            // Mettre à jour la première date proposée
            editableProspect.value.datesProposes[0] = {
              ...editableProspect.value.datesProposes[0],
              date: dateRdvISO,
              dateSortie: dateRdvISO,
              heureDebut: editableDateRdv.value.heureDebut,
              heureFin: editableDateRdv.value.heureFin
            }
          }
          
          console.log('📅 datesProposes après mise à jour:', editableProspect.value.datesProposes)
        }
        
        const response = await fetch(`/api/prospects/${editableProspect.value._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editableProspect.value)
        })

        if (response.ok) {
          const result = await response.json()
          toast.success('✅ Prospect mis à jour avec succès')
          emit('prospect-updated', result.prospect)
          closeModal()
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de la mise à jour')
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
        toast.error('Erreur de connexion au serveur')
      } finally {
        saving.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    // Utilitaires d'affichage
    const getStatutBadgeClass = (statut) => {
      const classes = {
        'En attente': 'badge badge-warning',
        'Validé': 'badge badge-success',
        'Rejeté': 'badge badge-danger',
        'Planifié': 'badge badge-info'
      }
      return classes[statut] || 'badge badge-secondary'
    }

    const getStatutIcon = (statut) => {
      const icons = {
        'En attente': '⏳',
        'Validé': '✅',
        'Rejeté': '❌',
        'Planifié': '📅'
      }
      return icons[statut] || '📋'
    }

    const getTechnicienName = (technicienId) => {
      const technicien = techniciens.value.find(t => t._id === technicienId)
      return technicien ? `${technicien.prenom} ${technicien.nom}` : null
    }

    // Fonction pour afficher le nom du technicien avec fallback sur les données du prospect
    const getDisplayTechnicienName = () => {
      const techId = editableProspect.value?.technicienAffecte
      
      // Si pas de technicien affecté
      if (!techId) {
        return 'Aucun technicien affecté'
      }
      
      // Chercher dans la liste des techniciens
      const technicien = techniciens.value.find(t => t._id === techId)
      if (technicien) {
        return `${technicien.prenom} ${technicien.nom}`
      }
      
      // Fallback: utiliser les données populées du prospect original
      if (props.prospect?.technicienAffecte && typeof props.prospect.technicienAffecte === 'object') {
        const tech = props.prospect.technicienAffecte
        return `${tech.prenom || ''} ${tech.nom || ''}`.trim() || 'Technicien affecté'
      }
      
      return 'Technicien affecté'
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getAppareilIcon = (type) => {
      const icons = {
        'GPS': '📡',
        'SIM': '📱',
        'Cable': '🔌',
        'Boitier': '📦',
        'Antenne': '📶',
        'Capteur': '🔍'
      }
      return icons[type] || '📦'
    }

    const getTotalAppareils = () => {
      if (!editableProspect.value.appareils || editableProspect.value.appareils.length === 0) {
        return 0
      }
      return editableProspect.value.appareils.reduce((total, app) => total + app.quantite, 0)
    }

    // Gestion des créneaux
    const addNewCreneau = () => {
      if (!editableProspect.value.datesProposes) {
        editableProspect.value.datesProposes = []
      }
      
      // Créer un nouveau créneau avec date du jour + 1
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      const dateStr = formatDateForInput(tomorrow)
      
      editableProspect.value.datesProposes.push({
        date: dateStr,
        dateSortie: dateStr, // Date réelle du RDV
        heureDebut: '09:00',
        heureFin: '12:00',
        disponible: true
      })
    }

    const removeCreneau = (index) => {
      if (confirm('Êtes-vous sûr de vouloir supprimer ce créneau ?')) {
        editableProspect.value.datesProposes.splice(index, 1)
      }
    }

    // Fonction pour obtenir les informations de la date RDV
    const getDateRdvInfo = () => {
      const prospect = props.prospect
      if (!prospect) return null
      
      // Priorité 1 : RDV existant (tous statuts - Planifié, Terminé, etc.)
      if (prospect.rdvExistant) {
        return {
          dateSortie: prospect.rdvExistant.dateRdv,
          heureDebut: prospect.rdvExistant.heureDebut,
          heureFin: prospect.rdvExistant.heureFin
        }
      }
      
      // Priorité 2 : Dates proposées
      if (!prospect.datesProposes || prospect.datesProposes.length === 0) return null
      
      // Retourner la première date proposée avec dateSortie
      const premiereDatePropose = prospect.datesProposes[0]
      return {
        dateSortie: premiereDatePropose.dateSortie || premiereDatePropose.date,
        heureDebut: premiereDatePropose.heureDebut,
        heureFin: premiereDatePropose.heureFin
      }
    }

    // Fonction pour formater l'affichage de la date RDV
    const formatDateRdvDisplay = (dateRdv) => {
      if (!dateRdv) return ''
      const dateValue = dateRdv.dateSortie || dateRdv.date
      if (!dateValue) return ''
      const date = new Date(dateValue)
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
    }

    // Gestion des appareils
    const addNewAppareil = () => {
      if (!editableProspect.value.appareils) {
        editableProspect.value.appareils = []
      }
      editableProspect.value.appareils.push({
        type: 'GPS',
        quantite: 1
      })
    }

    const removeAppareil = (index) => {
      if (confirm('Êtes-vous sûr de vouloir supprimer cet appareil ?')) {
        editableProspect.value.appareils.splice(index, 1)
      }
    }

    onMounted(() => {
      initializeData()
      loadTechniciens()
    })

    // Réinitialiser les données si le prospect change
    watch(() => props.prospect, (newProspect) => {
      if (newProspect) {
        initializeData()
      }
    }, { immediate: true })

    return {
      expandedSections,
      toggleSection,
      editableProspect,
      editableDateRdv,
      saving,
      canEdit,
      techniciens,
      saveChanges,
      closeModal,
      getStatutBadgeClass,
      getStatutIcon,
      getTechnicienName,
      getDisplayTechnicienName,
      formatDate,
      formatDateTime,
      getAppareilIcon,
      getTotalAppareils,
      addNewCreneau,
      removeCreneau,
      addNewAppareil,
      removeAppareil,
      getDateRdvInfo,
      formatDateRdvDisplay
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 24px;
  width: 100%;
  max-width: 1000px;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-bottom: none;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  border-radius: 24px 24px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent);
  pointer-events: none;
}

.header-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.prospect-id {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
  font-weight: 500;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 2rem;
  flex: 1;
  overflow-y: auto;
}

.section {
  margin-bottom: 2rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-left: 4px solid var(--primary-blue);
  overflow: hidden;
  transition: all 0.3s ease;
}

.section:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 27, 183, 0.3);
}

.section-collapsed {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-collapsed:hover {
  transform: translateY(0);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  user-select: none;
  position: relative;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.02));
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.section-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
}

.section-header:hover {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.08), rgba(0, 70, 255, 0.04));
}

.section-header h4 {
  flex: 1;
}

.section-toggle-icon {
  margin-left: auto;
  color: var(--primary-blue);
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.section-collapsed .section-toggle-icon {
  transform: rotate(-90deg);
}

.section-content {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--primary-blue);
}

.section-content {
  padding: 1.5rem;
}

.form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  flex: 1;
}

.form-group.full-width {
  flex: 1 1 100%;
  width: 100%;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 12px;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-control:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
  background: rgba(255, 255, 255, 0.95);
  transform: translateY(-1px);
}

.form-control.readonly {
  background: rgba(248, 250, 252, 0.8);
  color: #6b7280;
  cursor: not-allowed;
}

.form-control:disabled {
  background: rgba(248, 250, 252, 0.8);
  color: #6b7280;
  cursor: not-allowed;
}

.status-display, .technicien-display, .info-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  font-weight: 500;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.badge-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.badge-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.badge-info {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.badge-secondary {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
}

.dates-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.date-item {
  padding: 1rem;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.date-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.date-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--primary-blue);
}

.date-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.9rem;
}

.date-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
}

.date-status.available {
  color: #10b981;
}

.date-status:not(.available) {
  color: #ef4444;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-top: 1px solid rgba(226, 232, 240, 0.5);
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(255, 255, 255, 0.9));
  backdrop-filter: blur(15px);
  border-radius: 0 0 24px 24px;
}

.footer-left, .footer-right {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.readonly-notice {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(108, 117, 125, 0.1);
  color: #6c757d;
  border-radius: 12px;
  font-weight: 500;
  font-size: 0.9rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.85rem;
}

.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn:hover::before {
  left: 100%;
}

.btn-secondary {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white !important;
  border: 2px solid white !important;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}

.btn-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
  color: white !important;
  border: 2px solid white !important;
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
}

.btn-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

/* Styles pour la section des appareils */
.appareils-section {
  margin-bottom: 1.5rem;
}

.appareils-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.appareils-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 1rem;
  margin-bottom: 0;
}

.appareils-label i {
  font-size: 1.1rem;
  color: var(--secondary-blue);
}

.btn-add-appareil {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.btn-add-appareil:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
}

.no-appareils {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.8), rgba(241, 245, 249, 0.6));
  border-radius: 12px;
  border: 2px dashed rgba(203, 213, 225, 0.8);
}

.no-appareils i {
  font-size: 2.5rem;
  color: rgba(148, 163, 184, 0.5);
  margin-bottom: 0.75rem;
}

.no-appareils p {
  margin: 0 0 1rem 0;
  color: #64748b;
  font-size: 0.95rem;
}

.btn-add-first-appareil {
  padding: 0.6rem 1.2rem;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.appareils-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.appareil-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(5, 150, 105, 0.02));
  border-radius: 12px;
  border: 2px solid rgba(16, 185, 129, 0.15);
  transition: all 0.3s ease;
}

.appareil-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.3);
}

.appareil-icon-display {
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.1));
  border-radius: 10px;
  flex-shrink: 0;
}

.appareil-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.appareil-type {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
}

.appareil-qty {
  font-size: 0.85rem;
  color: #6b7280;
}

.appareil-qty strong {
  color: #059669;
  font-weight: 700;
}

/* Formulaire d'édition des appareils */
.appareil-edit-form {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.appareil-type-select {
  flex: 1;
  min-width: 140px;
  max-width: 200px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
}

.appareil-qty-input {
  width: 80px;
  text-align: center;
  padding: 0.6rem 0.5rem;
  font-size: 0.9rem;
}

.btn-remove-appareil {
  padding: 0.6rem;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
}

.btn-remove-appareil:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-remove-appareil i {
  font-size: 0.9rem;
}

/* Styles pour le sélecteur de technicien */
.technicien-select-wrapper {
  position: relative;
}

.technicien-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23001BB7' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 3rem;
  cursor: pointer;
  font-weight: 500;
}

.technicien-select:hover {
  border-color: var(--secondary-blue);
  box-shadow: 0 0 0 3px rgba(0, 70, 255, 0.1);
}

/* Styles pour les créneaux éditables */
.section-header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-add-creneau {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.2);
}

.btn-add-creneau:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.3);
}

.btn-add-creneau i {
  font-size: 0.9rem;
}

.date-item-editable {
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 2px solid rgba(0, 27, 183, 0.1);
  transition: all 0.3s ease;
  margin-bottom: 1rem;
}

.date-item-editable:hover {
  border-color: rgba(0, 27, 183, 0.2);
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.1);
}

.date-item-editable.readonly-date {
  padding: 1rem;
  background: rgba(248, 250, 252, 0.5);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.date-edit-form {
  width: 100%;
}

.date-edit-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.5fr auto;
  gap: 1rem;
  align-items: end;
}

.date-edit-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.date-edit-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary-blue);
  margin-bottom: 0;
}

.date-edit-group label i {
  font-size: 0.9rem;
  color: var(--secondary-blue);
}

.date-input,
.time-input {
  padding: 0.75rem;
  border: 2px solid rgba(226, 232, 240, 0.8);
  border-radius: 10px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.3s ease;
  font-weight: 500;
}

.date-input:focus,
.time-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
  transform: translateY(-1px);
}

/* Toggle Switch pour disponibilité */
.disponibilite-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
  cursor: pointer;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  border-radius: 26px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.toggle-slider:before {
  content: "";
  position: absolute;
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(24px);
}

.toggle-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #ef4444;
  transition: color 0.3s ease;
}

.toggle-label.active {
  color: #10b981;
}

/* Bouton supprimer créneau */
.date-edit-actions {
  display: flex;
  align-items: flex-end;
  padding-bottom: 0.5rem;
}

.btn-remove-creneau {
  padding: 0.75rem;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
}

.btn-remove-creneau:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
}

.btn-remove-creneau i {
  font-size: 1rem;
}

/* État vide des créneaux */
.no-dates {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.8), rgba(241, 245, 249, 0.6));
  border-radius: 16px;
  border: 2px dashed rgba(203, 213, 225, 0.8);
}

.no-dates i {
  font-size: 3rem;
  color: rgba(148, 163, 184, 0.5);
  margin-bottom: 1rem;
}

.no-dates p {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  font-size: 1rem;
  font-weight: 500;
}

.btn-add-first {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.2);
}

.btn-add-first:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(0, 27, 183, 0.3);
}

/* Responsive */
@media (max-width: 1200px) {
  .date-edit-row {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  
  .date-edit-actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
    padding-bottom: 0;
    padding-top: 0.5rem;
  }
}

@media (max-width: 768px) {
  .modal-content {
    margin: 0.5rem;
    max-height: 98vh;
    border-radius: 16px;
  }
  
  .modal-header {
    padding: 1.5rem;
    border-radius: 16px 16px 0 0;
  }
  
  .modal-body {
    padding: 1.5rem;
  }
  
  .section-header {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
    padding: 1rem;
  }
  
  .btn-add-creneau {
    width: 100%;
    justify-content: center;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .date-edit-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .date-edit-actions {
    grid-column: 1;
    justify-content: center;
  }
  
  .appareils-grid {
    grid-template-columns: 1fr;
  }
  
  .appareil-card {
    padding: 0.75rem;
  }
  
  .appareil-icon-display {
    width: 45px;
    height: 45px;
    font-size: 1.75rem;
  }
  
  .modal-footer {
    padding: 1.5rem;
    flex-direction: column;
    gap: 1rem;
  }
  
  .footer-left, .footer-right {
    width: 100%;
    justify-content: center;
  }
}

/* ========================================
   Gouvernorat Selector - Professional Design
   ======================================== */
.gouvernorat-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.03), rgba(37, 99, 235, 0.02));
  border: 2px solid rgba(59, 130, 246, 0.15);
  border-radius: 16px;
  padding: 0.25rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.08);
  overflow: hidden;
}

.gouvernorat-select-wrapper.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: rgba(248, 250, 252, 0.8);
}

.gouvernorat-select-wrapper:not(.disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
}

.gouvernorat-select-wrapper:not(.disabled):focus-within {
  transform: translateY(-3px);
  border-color: #3b82f6;
  box-shadow: 0 16px 50px rgba(59, 130, 246, 0.2), 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.gouvernorat-select-wrapper:focus-within .gouvernorat-select-arrow i {
  transform: rotate(180deg);
}

.gouvernorat-select-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  flex-shrink: 0;
  z-index: 2;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
}

.gouvernorat-select-wrapper.has-selection .gouvernorat-select-icon {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
}

.gouvernorat-select-wrapper select {
  flex: 1;
  background: transparent !important;
  border: none !important;
  padding: 0.6rem 0.75rem !important;
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  z-index: 2;
  min-height: 44px;
}

.gouvernorat-select-wrapper select:focus {
  outline: none;
  box-shadow: none !important;
  border: none !important;
}

.gouvernorat-select-wrapper.has-selection select {
  color: #2563eb;
  font-weight: 600;
}

.gouvernorat-select-wrapper select option {
  background: white;
  color: #333;
  padding: 10px 14px;
  font-weight: 500;
}

.gouvernorat-select-wrapper select optgroup {
  font-weight: 700;
  color: #1e40af;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05));
}

.gouvernorat-select-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05));
  border-radius: 10px;
  color: #3b82f6;
  font-size: 0.85rem;
  flex-shrink: 0;
  z-index: 2;
  margin-right: 0.25rem;
  transition: all 0.3s ease;
}

.gouvernorat-select-arrow i {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.gouvernorat-select-wrapper.has-selection .gouvernorat-select-arrow {
  color: #2563eb;
}

.gouvernorat-selection-indicator {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 22px;
  height: 22px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.65rem;
  z-index: 5;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

/* Styles pour l'affichage de la Date RDV */
.date-rdv-display {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.05));
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.date-rdv-main {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  color: #059669;
}

.date-rdv-main i {
  font-size: 1.2rem;
  color: #10b981;
}

.date-rdv-main .date-value {
  text-transform: capitalize;
}

.date-rdv-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #047857;
  padding-left: 2rem;
}

.date-rdv-time i {
  color: #10b981;
}

.no-date-rdv {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05));
  border-radius: 12px;
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #dc2626;
  font-weight: 500;
}

.no-date-rdv i {
  font-size: 1.2rem;
  color: #ef4444;
}

/* Styles pour le formulaire d'édition de la Date RDV */
.date-rdv-edit-form {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(37, 99, 235, 0.04));
  border-radius: 16px;
  padding: 1.25rem;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.date-rdv-edit-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  gap: 1rem;
  align-items: end;
}

.date-rdv-edit-form .date-edit-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.date-rdv-edit-form .date-edit-group label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-rdv-edit-form .date-edit-group label i {
  color: #3b82f6;
  font-size: 0.9rem;
}

.date-rdv-edit-form .date-input,
.date-rdv-edit-form .time-input {
  padding: 0.75rem 1rem;
  border: 2px solid rgba(59, 130, 246, 0.2);
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #1f2937;
  background: white;
  transition: all 0.2s ease;
}

.date-rdv-edit-form .date-input:focus,
.date-rdv-edit-form .time-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.date-rdv-edit-form .date-input:hover,
.date-rdv-edit-form .time-input:hover {
  border-color: rgba(59, 130, 246, 0.4);
}

@media (max-width: 768px) {
  .date-rdv-edit-row {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
}
</style>
